// BKLS rights, workflow, wizard and SEO — client-safe shared logic.
// Pure functions only: imported by both admin UI and server functions so the
// validation a steward sees in the wizard is the validation the server applies.

export type WorkflowStatus =
  | "draft"
  | "internal_review"
  | "legal_review"
  | "ready"
  | "scheduled"
  | "published"
  | "archived"
  | "retired";

export const WORKFLOW_ORDER: WorkflowStatus[] = [
  "draft",
  "internal_review",
  "legal_review",
  "ready",
  "scheduled",
  "published",
  "archived",
  "retired",
];

export const WORKFLOW_LABEL: Record<WorkflowStatus, string> = {
  draft: "Draft",
  internal_review: "Internal review",
  legal_review: "Legal review",
  ready: "Ready",
  scheduled: "Scheduled",
  published: "Published",
  archived: "Archived",
  retired: "Retired",
};

/** Mirrors the database transition guard — no direct draft → published. */
export const WORKFLOW_TRANSITIONS: Record<WorkflowStatus, WorkflowStatus[]> = {
  draft: ["internal_review", "archived"],
  internal_review: ["draft", "legal_review", "archived"],
  legal_review: ["internal_review", "ready", "archived"],
  ready: ["scheduled", "published", "legal_review", "archived"],
  scheduled: ["published", "ready", "archived"],
  published: ["archived", "retired"],
  archived: ["draft", "retired"],
  retired: ["archived"],
};

export function canTransition(from: WorkflowStatus, to: WorkflowStatus): boolean {
  return (WORKFLOW_TRANSITIONS[from] ?? []).includes(to);
}

// ---------------- Rights ledger ----------------

export const RIGHTS_STATUS = ["unverified", "verified", "disputed", "restricted"] as const;
export const SAMPLE_STATUS = ["original", "sampled_cleared", "sampled_uncleared", "royalty_free"] as const;
export const CLEARANCE_STATUS = ["pending", "cleared", "blocked"] as const;

export type BeatRights = {
  beat_id: string;
  rights_status: string;
  copyright_owner: string | null;
  master_owner: string | null;
  composition_owner: string | null;
  publisher: string | null;
  writers: string[];
  producer: string | null;
  collaborators: string[];
  pro: string | null;
  iswc: string | null;
  isrc: string | null;
  copyright_registration_number: string | null;
  sample_status: string;
  clearance_status: string;
  notes: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
};

/** Extension points for Module 16 — one rights engine, many work kinds. */
export const WORK_KINDS = [
  "beat",
  "song",
  "film_sync",
  "tv_sync",
  "advertising",
  "podcast",
  "meditation",
  "game",
  "custom_production",
] as const;
export type WorkKind = (typeof WORK_KINDS)[number];

// ---------------- Per-beat license configuration ----------------

export const LICENSE_TIER_KEYS = ["basic", "premium", "unlimited", "custom", "exclusive"] as const;

export type BeatLicenseConfig = {
  id?: string;
  beat_id: string;
  tier_key: string;
  enabled: boolean;
  price_cents: number | null;
  download_package: string[];
  stream_limit: string | null;
  video_limit: string | null;
  performance_rights: string | null;
  monetization: string | null;
  sync_allowed: boolean;
  credit_requirement: string | null;
  territory: string;
  duration: string;
  agreement_version: string;
};

export const CURRENT_AGREEMENT_VERSION = "BKLS License v1.0";

// ---------------- File validation ----------------

export const ALLOWED_AUDIO_CODECS = ["mp3", "wav", "aiff", "flac"] as const;
export const BLOCKED_EXTENSIONS = [
  "exe", "dll", "sh", "bat", "cmd", "com", "scr", "js", "jar", "app", "msi", "svg",
];

export type FileProbe = {
  name: string;
  sizeBytes: number;
  checksum?: string | null;
  codec?: string | null;
  bitrateKbps?: number | null;
  sampleRate?: number | null;
  channels?: number | null;
  durationSeconds?: number | null;
};

export function validateBeatFile(
  probe: FileProbe,
  knownChecksums: string[] = [],
): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  const ext = probe.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ext) errors.push("File has no extension.");
  if (BLOCKED_EXTENSIONS.includes(ext)) errors.push(`Executable or unsafe file type rejected (.${ext}).`);
  if (!probe.sizeBytes || probe.sizeBytes < 1024) errors.push("File is empty or corrupt.");
  if (probe.codec && !ALLOWED_AUDIO_CODECS.includes(probe.codec.toLowerCase() as never)) {
    errors.push(`Unsupported codec: ${probe.codec}.`);
  }
  if (probe.checksum && knownChecksums.includes(probe.checksum)) {
    errors.push("A file with an identical checksum already exists in the catalog.");
  }
  return { ok: errors.length === 0, errors };
}

// ---------------- Wizard ----------------

export const WIZARD_STEPS = [
  { key: "metadata", label: "Metadata" },
  { key: "rights", label: "Rights" },
  { key: "artwork", label: "Artwork" },
  { key: "preview", label: "Preview" },
  { key: "downloads", label: "Downloads" },
  { key: "licenses", label: "Licenses" },
  { key: "seo", label: "SEO" },
  { key: "review", label: "Review" },
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number]["key"];

export type WizardDraft = {
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  bpm?: number | null;
  musical_key?: string | null;
  genre?: string | null;
  mood?: string | null;
  tags: string[];
  collection_slug?: string | null;
  artwork_path?: string | null;
  preview_path?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
};

export type WizardContext = {
  fileKinds: string[];
  licenses: Array<{ tier_key: string; enabled: boolean; price_cents: number | null }>;
  rights?: Partial<BeatRights> | null;
  existingSlugs?: string[];
};

export type WizardIssue = { step: WizardStepKey; field: string; message: string };

const KEY_PATTERN = /^[A-G](#|b)?\s?(major|minor|maj|min|m)?$/i;

export function validateWizard(draft: WizardDraft, ctx: WizardContext): WizardIssue[] {
  const issues: WizardIssue[] = [];
  const push = (step: WizardStepKey, field: string, message: string) =>
    issues.push({ step, field, message });

  if (!draft.title?.trim()) push("metadata", "title", "Title is required.");
  if (!/^[a-z0-9-]+$/.test(draft.slug ?? "")) push("metadata", "slug", "Slug must be lowercase letters, numbers and dashes.");
  if ((ctx.existingSlugs ?? []).includes(draft.slug)) push("metadata", "slug", "Duplicate slug — another beat already uses it.");
  if (!draft.description?.trim() || draft.description.trim().length < 40) {
    push("metadata", "description", "Description must be at least 40 characters.");
  }
  if (!draft.tags?.length) push("metadata", "tags", "At least one tag is required.");
  if (draft.bpm == null || Number.isNaN(draft.bpm) || draft.bpm < 40 || draft.bpm > 300) {
    push("metadata", "bpm", "BPM must be between 40 and 300.");
  }
  if (!draft.musical_key?.trim() || !KEY_PATTERN.test(draft.musical_key.trim())) {
    push("metadata", "musical_key", "Musical key must look like 'F# minor' or 'C major'.");
  }

  const rights = ctx.rights ?? {};
  if (!rights.copyright_owner) push("rights", "copyright_owner", "Copyright owner is required.");
  if (!rights.master_owner) push("rights", "master_owner", "Master owner is required.");
  if (!rights.composition_owner) push("rights", "composition_owner", "Composition owner is required.");
  if (!rights.producer) push("rights", "producer", "Producer is required.");
  if (rights.clearance_status && rights.clearance_status === "blocked") {
    push("rights", "clearance_status", "Clearance is blocked — this work cannot be published.");
  }
  if (rights.sample_status === "sampled_uncleared") {
    push("rights", "sample_status", "Uncleared samples must be cleared before publishing.");
  }

  if (!draft.artwork_path) push("artwork", "artwork_path", "Artwork is required.");
  if (!draft.preview_path) push("preview", "preview_path", "A tagged preview is required.");
  if (!ctx.fileKinds.includes("wav")) push("downloads", "wav", "A master WAV file is required.");

  const enabled = (ctx.licenses ?? []).filter((l) => l.enabled);
  if (!enabled.length) push("licenses", "licenses", "At least one licence must be enabled.");
  for (const l of enabled) {
    if (l.tier_key !== "custom" && l.tier_key !== "exclusive" && (l.price_cents ?? 0) < 100) {
      push("licenses", l.tier_key, `${l.tier_key} licence needs a price of at least $1.`);
    }
  }

  const seo = buildBeatSeo(draft);
  if (seo.title.length > 60) push("seo", "seo_title", "SEO title must be 60 characters or fewer.");
  if (seo.description.length > 160) push("seo", "seo_description", "Meta description must be 160 characters or fewer.");

  return issues;
}

export function isStepComplete(step: WizardStepKey, issues: WizardIssue[]): boolean {
  return !issues.some((i) => i.step === step);
}

// ---------------- SEO engine ----------------

export function buildBeatSeo(draft: WizardDraft) {
  const facets = [draft.genre, draft.mood, draft.bpm ? `${draft.bpm} BPM` : null, draft.musical_key]
    .filter(Boolean)
    .join(" · ");
  const title = (draft.seo_title?.trim() || `${draft.title} — ${draft.genre ?? "Beat"} Beat`).slice(0, 60);
  const base =
    draft.seo_description?.trim() ||
    `License ${draft.title}${facets ? ` (${facets})` : ""} — an original Buddha KA production. Instant licence terms, untagged masters.`;
  return { title, description: base.slice(0, 160), facets };
}

export function beatJsonLd(params: {
  slug: string;
  title: string;
  description: string;
  url: string;
  imageUrl?: string | null;
  genre?: string | null;
  bpm?: number | null;
  isrc?: string | null;
  iswc?: string | null;
  durationSeconds?: number | null;
  datePublished?: string | null;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: params.title,
    description: params.description,
    url: params.url,
    ...(params.imageUrl ? { image: params.imageUrl } : {}),
    ...(params.genre ? { genre: params.genre } : {}),
    ...(params.isrc ? { isrcCode: params.isrc } : {}),
    ...(params.iswc ? { iswcCode: params.iswc } : {}),
    ...(params.durationSeconds ? { duration: `PT${Math.round(params.durationSeconds)}S` } : {}),
    ...(params.datePublished ? { datePublished: params.datePublished } : {}),
    ...(params.bpm ? { additionalProperty: [{ "@type": "PropertyValue", name: "BPM", value: params.bpm }] } : {}),
    byArtist: { "@type": "MusicGroup", name: "Buddha KA" },
    inAlbum: undefined,
  };
}

export function breadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ---------------- Duplicate detection ----------------

export type DuplicateWarning = { field: string; message: string; matchSlug: string };

export function detectDuplicates(
  candidate: { id?: string; slug: string; title: string; artwork_path?: string | null; preview_path?: string | null; checksums?: string[] },
  existing: Array<{
    id: string;
    slug: string;
    title: string;
    artwork_path: string | null;
    preview_path: string | null;
    checksums?: string[];
  }>,
): DuplicateWarning[] {
  const out: DuplicateWarning[] = [];
  const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
  for (const row of existing) {
    if (candidate.id && row.id === candidate.id) continue;
    if (norm(row.slug) === norm(candidate.slug)) out.push({ field: "slug", message: "Slug already in use.", matchSlug: row.slug });
    if (norm(row.title) === norm(candidate.title)) out.push({ field: "title", message: "Another beat has the same title.", matchSlug: row.slug });
    if (candidate.artwork_path && row.artwork_path === candidate.artwork_path)
      out.push({ field: "artwork", message: "Artwork already used by another beat.", matchSlug: row.slug });
    if (candidate.preview_path && row.preview_path === candidate.preview_path)
      out.push({ field: "preview", message: "Preview already used by another beat.", matchSlug: row.slug });
    const shared = (candidate.checksums ?? []).filter((c) => (row.checksums ?? []).includes(c));
    if (shared.length) out.push({ field: "checksum", message: "Identical audio file checksum.", matchSlug: row.slug });
  }
  return out;
}

// ---------------- Analytics ----------------

export type BeatAnalyticsSummary = {
  beat_slug: string;
  views: number;
  uniqueVisitors: number;
  previewPlays: number;
  previewCompletes: number;
  completionRate: number;
  licenseClicks: number;
  sales: number;
  revenueCents: number;
  conversionRate: number;
  countries: Array<{ country: string; count: number }>;
  sources: Array<{ source: string; count: number }>;
};

export function summarizeBeatEvents(
  slug: string,
  events: Array<{
    event_type: string;
    visitor_hash: string | null;
    country: string | null;
    referrer_host: string | null;
    amount_cents: number | null;
  }>,
): BeatAnalyticsSummary {
  const count = (t: string) => events.filter((e) => e.event_type === t).length;
  const views = count("view");
  const previewPlays = count("preview_play");
  const previewCompletes = count("preview_complete");
  const sales = count("purchase");
  const revenueCents = events
    .filter((e) => e.event_type === "purchase")
    .reduce((sum, e) => sum + (e.amount_cents ?? 0), 0);
  const tally = (key: "country" | "referrer_host") => {
    const map = new Map<string, number>();
    for (const e of events) {
      const v = e[key];
      if (!v) continue;
      map.set(v, (map.get(v) ?? 0) + 1);
    }
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([k, v]) => ({ country: k, source: k, count: v }));
  };
  return {
    beat_slug: slug,
    views,
    uniqueVisitors: new Set(events.map((e) => e.visitor_hash).filter(Boolean)).size,
    previewPlays,
    previewCompletes,
    completionRate: previewPlays ? Math.round((previewCompletes / previewPlays) * 100) : 0,
    licenseClicks: count("license_click"),
    sales,
    revenueCents,
    conversionRate: views ? Math.round((sales / views) * 10000) / 100 : 0,
    countries: tally("country").map(({ country, count }) => ({ country, count })),
    sources: tally("referrer_host").map(({ source, count }) => ({ source, count })),
  };
}
